import {
    useFloating, autoUpdate, offset, flip, shift, useHover, useFocus, useDismiss, useRole, useInteractions,
    useMergeRefs, FloatingPortal, Placement,
} from '@floating-ui/react'

import { useMemo, useState, createContext, useContext, ReactNode, HTMLProps, isValidElement, cloneElement } from 'react'

interface TooltipOptions
{
    initialOpen?: boolean;
    placement?: Placement;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

/* ───────────────────────────── hooks ────────────────────────────── */
export function useTooltip ({
    initialOpen = false,
    placement = 'top',
    open: controlledOpen,
    onOpenChange: setControlledOpen,
}: TooltipOptions = {})
{
    const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen)

    const open = controlledOpen ?? uncontrolledOpen
    const setOpen = setControlledOpen ?? setUncontrolledOpen

    const data = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [offset(5), flip({
            crossAxis: placement.includes('-'),
            fallbackAxisSideDirection: 'start',
            padding: 5,
        }), shift({ padding: 5 })],
    })

    const context = data.context

    const hover = useHover(context, {
        move: false,
        enabled: controlledOpen == null,
    })
    const focus = useFocus(context, { enabled: controlledOpen == null })
    const dismiss = useDismiss(context)
    const role = useRole(context, { role: 'tooltip' })

    const interactions = useInteractions([hover, focus, dismiss, role])

    return useMemo(() => (
        {
            open,
            setOpen, ...interactions, ...data,
        }
    ), [open, setOpen, interactions, data])
}

/* ─────────────────────── context provider ───────────────────────── */
type ContextType = ReturnType<typeof useTooltip> | null;
const TooltipContext = createContext<ContextType>(null)

export const useTooltipContext = () =>
{
    const ctx = useContext(TooltipContext)
    if (ctx == null) {
        throw new Error('Tooltip components must be wrapped in <Tooltip />')
    }
    return ctx
}

/* ────────────────────────── components ──────────────────────────── */

export function Tooltip (props: { children: ReactNode } & TooltipOptions)
{
    const {
        children,
        ...options
    } = props
    const tooltip = useTooltip(options)
    return (
        <TooltipContext.Provider value={tooltip}>
            {children}
        </TooltipContext.Provider>
    )
}

interface TooltipTriggerProps extends HTMLProps<HTMLElement>
{
    asChild?: boolean;
    children: ReactNode;
}

export function TooltipTrigger ({
    asChild = false,
    children,
    ...props
}: TooltipTriggerProps)
{
    const context = useTooltipContext()

    // merge Floating-UI ref with child ref (if `asChild`)
    const childRef = (
        children as any
    )?.ref
    const ref = useMergeRefs([context.refs.setReference, childRef])

    if (asChild && isValidElement(children)) {
        return cloneElement(children, context.getReferenceProps({
            ref, ...props, ...(
                children as any
            ).props,
            'data-state': context.open
                ? 'open'
                : 'closed',
        }))
    }

    return (
        <button
            ref={ref}
            data-state={context.open
                ? 'open'
                : 'closed'}{...context.getReferenceProps(props)}>
            {children}
        </button>
    )
}

interface TooltipContentProps extends HTMLProps<HTMLDivElement> {}

export function TooltipContent ({
    style,
    ...props
}: TooltipContentProps)
{
    const context = useTooltipContext()
    const ref = useMergeRefs([context.refs.setFloating])

    if (!context.open) {
        return null
    }

    return (
        <FloatingPortal>
            <div
                ref={ref}
                style={{ ...context.floatingStyles, ...style }}{...context.getFloatingProps(props)}
            />
        </FloatingPortal>
    )
}